import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../hooks/useJobs';
import { getAllUsers } from '../firebase/db';
import { uploadPhoto } from '../firebase/storage';
import { 
  searchZohoCustomers, 
  transformZohoToJob, 
  isZohoConnected, 
  connectZohoAccount,
  getZohoEstimates,
  getZohoEstimateDetails,
  getZohoInvoices,
  getZohoInvoiceDetails
} from '../firebase/zoho-oauth';
import './Jobs.css';

const NewJob = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { addJob } = useJobs();
  const location = useLocation();
  const cloneJob = location.state?.cloneJob;

  const [workers, setWorkers] = useState([]);
  
  // Zoho search state
  const [zohoSearch, setZohoSearch] = useState('');
  const [zohoCustomers, setZohoCustomers] = useState([]);
  const [showZohoResults, setShowZohoResults] = useState(false);
  const [zohoLoading, setZohoLoading] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [zohoConnecting, setZohoConnecting] = useState(false);
  const [zohoError, setZohoError] = useState('');
  
  // Zoho estimates state
  const [zohoEstimates, setZohoEstimates] = useState([]);
  const [zohoInvoices, setZohoInvoices] = useState([]);
  const [showEstimates, setShowEstimates] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [zohoDocType, setZohoDocType] = useState('estimate'); // 'estimate' | 'invoice'
  
  // Job photos
  const [jobPhotos, setJobPhotos] = useState([]);
  
  // Multiple workers
  const [assignedWorkers, setAssignedWorkers] = useState([]);
  
  const [form, setForm] = useState(() => {
    if (cloneJob) {
      const { id, status, createdAt, completedAt, jobPhotos, timesheets, ...cloneable } = cloneJob;
      return {
        ...cloneable,
        status: 'scheduled',
        scheduledDate: cloneJob.scheduledDate ? cloneJob.scheduledDate.split('T')[0] : '',
        scheduledTime: cloneJob.scheduledDate ? cloneJob.scheduledDate.split('T')[1]?.slice(0, 5) : '08:00',
        scheduledEndDate: cloneJob.scheduledEndDate ? (cloneJob.scheduledEndDate.includes('T') ? cloneJob.scheduledEndDate.split('T')[0] : cloneJob.scheduledEndDate) : '',
        isMultiDay: cloneJob.isMultiDay || false,
        assignedTo: [],
        assignedToName: [],
        jobPhotos: [],
        zohoSynced: false,
        zohoCustomerId: '',
        zohoEstimateId: '',
        zohoInvoiceId: '',
        zohoSyncDate: null,
        additionalInfo: 'No signed timesheet and before/after pictures = No pay',
      };
    }
    return {
    title: '',
    description: '',
    location: '',
    scheduledDate: '',
    scheduledTime: '08:00',
    scheduledEndDate: '',
    isMultiDay: false,
    assignedTo: [],
    assignedToName: [],
    status: 'scheduled',
    zohoCustomerId: '',
    companyName: '',
    clientContact: '',
    clientEmail: '',
    clientPhone: '',
    primarySiteContact: '',
    siteContactPhone: '',
    companyAddress: '',
    jobType: '',
    jobReference: '',
    jobDetails: '',
    additionalInfo: 'No signed timesheet and before/after pictures = No pay',
    zohoSynced: false,
    zohoEstimateId: '',
    zohoInvoiceId: '',
    };
  });

  const loadWorkers = async () => {
    const users = await getAllUsers();
    const labeled = users.map(u => ({
      ...u,
      label: `${u.displayName || 'Unknown'} ${u.role ? '(' + u.role + ')' : ''}`,
    }));
    setWorkers(labeled);
  };

  const checkZohoConnection = async () => {
    const connected = await isZohoConnected();
    setZohoConnected(connected);
  };

  useEffect(() => {
    if (isAdmin) loadWorkers();
    checkZohoConnection();
  }, [isAdmin]);

  // ─── Zoho Search ───
  const handleZohoSearch = async () => {
    if (!zohoSearch.trim()) return;

    const connected = await isZohoConnected();
    if (!connected) {
      setZohoConnected(false);
      return;
    }
    setZohoConnected(true);

    setZohoLoading(true);
    const { customers, error } = await searchZohoCustomers(zohoSearch);
    setZohoCustomers(customers);
    setShowZohoResults(true);
    setZohoLoading(false);
    if (error) console.error('Zoho search error:', error);
  };

  const handleConnectZoho = async () => {
    setZohoError('');
    setZohoConnecting(true);
    const { success, error } = await connectZohoAccount();
    setZohoConnecting(false);
    if (success) {
      setZohoConnected(true);
      setZohoError('');
      if (zohoSearch.trim()) {
        handleZohoSearch();
      }
    } else {
      setZohoError(error || 'Failed to connect to Zoho. Please check your configuration.');
      console.error('Zoho connection failed:', error);
    }
  };

  const handleSelectZohoCustomer = async (customer) => {
    setSelectedCustomer(customer);
    
    setEstimatesLoading(true);
    setInvoicesLoading(true);
    
    // Load both estimates and invoices in parallel
    const [{ estimates }, { invoices }] = await Promise.all([
      getZohoEstimates(customer.contact_id),
      getZohoInvoices(customer.contact_id)
    ]);
    
    setZohoEstimates(estimates);
    setZohoInvoices(invoices);
    setEstimatesLoading(false);
    setInvoicesLoading(false);
    
    setForm(prev => ({
      ...prev,
      zohoCustomerId: customer.contact_id || '',
      companyName: customer.contact_name || customer.company_name || '',
      clientContact: customer.contact_name || '',
      clientEmail: customer.email || '',
      clientPhone: customer.phone || customer.mobile || '',
      primarySiteContact: customer.billing_address?.attention || '',
      companyAddress: [
        customer.billing_address?.street1,
        customer.billing_address?.street2,
        customer.billing_address?.city,
        customer.billing_address?.state,
        customer.billing_address?.zip,
        customer.billing_address?.country,
      ].filter(Boolean).join(', ') || '',
      title: customer.contact_name || prev.title,
      location: '',
      zohoSynced: true,
    }));
    
    // Show both tabs if data exists
    setShowEstimates(estimates.length > 0);
    setShowInvoices(invoices.length > 0);
    if (estimates.length === 0 && invoices.length === 0) {
      setShowZohoResults(false);
      setZohoSearch('');
    }
  };

  const handleSelectEstimate = async (estimate) => {
    setSelectedEstimate(estimate);
    
    const { estimate: details, error } = await getZohoEstimateDetails(estimate.estimate_id);
    
    if (details && selectedCustomer) {
      const jobData = transformZohoToJob(selectedCustomer, details, 'estimate');
      
      setForm(prev => ({
        ...prev,
        ...jobData,
        title: jobData.title || prev.title,
        location: '',
        zohoEstimateId: estimate.estimate_id || '',
        zohoInvoiceId: '',
        // Preserve static additionalInfo
        additionalInfo: prev.additionalInfo || 'No signed timesheet and before/after pictures = No pay',
      }));
    }
    
    setShowEstimates(false);
    setShowInvoices(false);
    setShowZohoResults(false);
    setZohoSearch('');
  };

  const handleSelectInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    
    const { invoice: details, error } = await getZohoInvoiceDetails(invoice.invoice_id);
    
    if (details && selectedCustomer) {
      const jobData = transformZohoToJob(selectedCustomer, details, 'invoice');
      
      setForm(prev => ({
        ...prev,
        ...jobData,
        title: jobData.title || prev.title,
        location: '',
        zohoInvoiceId: invoice.invoice_id || '',
        zohoEstimateId: '',
        // Preserve static additionalInfo
        additionalInfo: prev.additionalInfo || 'No signed timesheet and before/after pictures = No pay',
      }));
    }
    
    setShowEstimates(false);
    setShowInvoices(false);
    setShowZohoResults(false);
    setZohoSearch('');
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9),
    }));
    setJobPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (photoId) => {
    setJobPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let uploadedPhotoUrls = [];
    if (jobPhotos.length > 0) {
      const uploadPromises = jobPhotos.map(async (p) => {
        if (p.file) {
          const path = `jobs/temp/${Date.now()}_${p.file.name}`;
          const { url, error } = await uploadPhoto(p.file, path);
          if (url) {
            return { url, caption: '', uploadedAt: new Date().toISOString() };
          }
          return { url: p.preview, caption: '', uploadedAt: new Date().toISOString() };
        }
        return { url: p.preview || p.url, caption: '', uploadedAt: new Date().toISOString() };
      });
      uploadedPhotoUrls = await Promise.all(uploadPromises);
    }
    
    const workerIds = assignedWorkers.map(w => w.uid);
    const workerNames = assignedWorkers.map(w => w.displayName || 'Unknown');
    
    const jobData = {
      ...form,
      assignedTo: workerIds,
      assignedToName: workerNames,
      jobPhotos: uploadedPhotoUrls,
      // Combine date and time into a full datetime string
      scheduledDate: form.scheduledDate && form.scheduledTime
        ? `${form.scheduledDate}T${form.scheduledTime}:00`
        : form.scheduledDate,
    };
    
    await addJob(jobData);
    navigate('/jobs');
  };

  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const addWorker = () => {
    setAssignedWorkers(prev => [...prev, { uid: '', displayName: '' }]);
  };

  const removeWorker = (index) => {
    setAssignedWorkers(prev => prev.filter((_, i) => i !== index));
  };

  const updateWorker = (index, uid) => {
    const worker = workers.find(w => w.uid === uid);
    setAssignedWorkers(prev => prev.map((w, i) => 
      i === index ? { uid: worker?.uid || '', displayName: worker?.displayName || '' } : w
    ));
  };

  return (
    <div className="page new-job">
      <div className="new-job-header">
        <h2>{cloneJob ? 'Clone Job' : 'Create New Job'}</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/jobs')}>
          Cancel
        </button>
      </div>

      {cloneJob && (
        <div className="clone-notice">
          <p>📋 Cloning from: <strong>{cloneJob.title}</strong> ({cloneJob.companyName || 'No company'})</p>
          <p className="clone-hint">Please review and update fields before saving.</p>
        </div>
      )}

      {/* Zoho Import Section — Admin Only */}
      {isAdmin && (
      <div className="zoho-import-section">
        <div className="zoho-import-header">
          <span className="zoho-import-icon">🔍</span>
          <span className="zoho-import-label">Import from Zoho Books</span>
        </div>
        <p className="zoho-import-desc">Connect to Zoho Books to import customers and estimates</p>
        
        {!zohoConnected ? (
          <>
            <button 
              className="zoho-connect-btn" 
              onClick={handleConnectZoho}
              disabled={zohoConnecting}
            >
              {zohoConnecting ? 'Connecting...' : 'Connect Zoho'}
            </button>
            {zohoError && (
              <p className="zoho-error" style={{ color: '#dc2626', fontSize: '14px', marginTop: '8px' }}>
                ⚠️ {zohoError}
              </p>
            )}
          </>
        ) : (
          <div className="zoho-search-box">
            <input
              type="text"
              placeholder="Search customer..."
              value={zohoSearch}
              onChange={(e) => setZohoSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleZohoSearch()}
            />
            <button 
              className="btn btn-primary"
              onClick={handleZohoSearch} 
              disabled={zohoLoading}
            >
              {zohoLoading ? 'Searching...' : 'Search Zoho'}
            </button>
          </div>
        )}

        {/* Zoho Customer Results */}
        {showZohoResults && zohoCustomers.length > 0 && (
          <div className="zoho-results">
            <p className="zoho-results-title">Select a customer:</p>
            {zohoCustomers.map((c) => (
              <div
                key={c.contact_id}
                className="zoho-result-item"
                onClick={() => handleSelectZohoCustomer(c)}
              >
                <strong>{c.contact_name}</strong>
                <br />
                <small>
                  {c.email && <span>{c.email}</span>}
                  {c.email && c.phone && <span> · </span>}
                  {c.phone && <span>{c.phone}</span>}
                </small>
              </div>
            ))}
          </div>
        )}

        {/* Zoho Estimates & Invoices Tabs */}
        {(zohoEstimates.length > 0 || zohoInvoices.length > 0) && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              {zohoEstimates.length > 0 && (
                <button 
                  className="zoho-resync-btn" 
                  style={{ 
                    background: zohoDocType === 'estimate' ? '#dbeafe' : '#f3f4f6',
                    color: zohoDocType === 'estimate' ? '#1e40af' : '#6b7280'
                  }}
                  onClick={() => setZohoDocType('estimate')}
                >
                  📋 Estimates ({zohoEstimates.length})
                </button>
              )}
              {zohoInvoices.length > 0 && (
                <button 
                  className="zoho-resync-btn" 
                  style={{ 
                    background: zohoDocType === 'invoice' ? '#dbeafe' : '#f3f4f6',
                    color: zohoDocType === 'invoice' ? '#1e40af' : '#6b7280'
                  }}
                  onClick={() => setZohoDocType('invoice')}
                >
                  🧾 Invoices ({zohoInvoices.length})
                </button>
              )}
            </div>
            
            {zohoDocType === 'estimate' && zohoEstimates.length > 0 && (
              <div className="zoho-estimates">
                <p className="zoho-estimates-title">Select an estimate (optional):</p>
                {zohoEstimates.map((est) => (
                  <div
                    key={est.estimate_id}
                    className="zoho-estimate-item"
                    onClick={() => handleSelectEstimate(est)}
                  >
                    <strong>{est.estimate_number || 'No #'}</strong>
                    <br />
                    <small>
                      {est.date && <span>{est.date}</span>}
                      {est.date && est.total && <span> · </span>}
                      {est.total && <span>£{est.total}</span>}
                    </small>
                  </div>
                ))}
                <button 
                  className="zoho-skip-btn"
                  onClick={() => {
                    setShowEstimates(false);
                    setShowInvoices(false);
                    setShowZohoResults(false);
                    setZohoSearch('');
                  }}
                >
                  Skip (no estimate)
                </button>
              </div>
            )}
            
            {zohoDocType === 'invoice' && zohoInvoices.length > 0 && (
              <div className="zoho-estimates">
                <p className="zoho-estimates-title">Select an invoice (optional):</p>
                {zohoInvoices.map((inv) => (
                  <div
                    key={inv.invoice_id}
                    className="zoho-estimate-item"
                    onClick={() => handleSelectInvoice(inv)}
                  >
                    <strong>{inv.invoice_number || 'No #'}</strong>
                    <br />
                    <small>
                      {inv.date && <span>{inv.date}</span>}
                      {inv.date && inv.total && <span> · </span>}
                      {inv.total && <span>£{inv.total}</span>}
                    </small>
                  </div>
                ))}
                <button 
                  className="zoho-skip-btn"
                  onClick={() => {
                    setShowEstimates(false);
                    setShowInvoices(false);
                    setShowZohoResults(false);
                    setZohoSearch('');
                  }}
                >
                  Skip (no invoice)
                </button>
              </div>
            )}
          </div>
        )}
        {(estimatesLoading || invoicesLoading) && <p className="zoho-loading">Loading documents...</p>}
      </div>
      )}

      <form onSubmit={handleSubmit} className="new-job-form">
        <div className="form-row">
          <label>Company / Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
          />
        </div>

        <div className="form-row">
          <label>Client Contact <span className="zoho-badge">Zoho</span></label>
          <input
            type="text"
            value={form.clientContact}
            onChange={(e) => handleInputChange('clientContact', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Client Email <span className="zoho-badge">Zoho</span></label>
          <input
            type="email"
            value={form.clientEmail}
            onChange={(e) => handleInputChange('clientEmail', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Client Phone <span className="zoho-badge">Zoho</span></label>
          <input
            type="tel"
            value={form.clientPhone}
            onChange={(e) => handleInputChange('clientPhone', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Primary Site Contact</label>
          <input
            type="text"
            value={form.primarySiteContact}
            onChange={(e) => handleInputChange('primarySiteContact', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Company Address / Location</label>
          <textarea
            value={form.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            rows={2}
          />
        </div>

        <div className="form-row">
          <label>Job Type</label>
          <input
            type="text"
            value={form.jobType}
            onChange={(e) => handleInputChange('jobType', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Job Reference</label>
          <input
            type="text"
            value={form.jobReference}
            onChange={(e) => handleInputChange('jobReference', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Job Details</label>
          <textarea
            value={form.jobDetails}
            onChange={(e) => handleInputChange('jobDetails', e.target.value)}
            rows={3}
          />
        </div>

        <div className="form-row">
          <label>Additional Info</label>
          <textarea
            value={form.additionalInfo}
            onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
            rows={2}
          />
        </div>

        <div className="form-row">
          <label>Scheduled Date</label>
          <input
            type="date"
            value={form.scheduledDate}
            onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Start Time</label>
          <input
            type="time"
            value={form.scheduledTime}
            onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
          />
        </div>

        <div className="form-row checkbox-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.isMultiDay}
              onChange={(e) => handleInputChange('isMultiDay', e.target.checked)}
            />
            Multi-Day Job (consecutive days)
          </label>
        </div>

        {form.isMultiDay && (
          <div className="form-row">
            <label>End Date</label>
            <input
              type="date"
              value={form.scheduledEndDate}
              min={form.scheduledDate}
              onChange={(e) => handleInputChange('scheduledEndDate', e.target.value)}
            />
          </div>
        )}

        <div className="form-row">
          <label>Assign to Fieldworkers</label>
          {assignedWorkers.map((worker, index) => (
            <div key={index} className="worker-select-row">
              <select
                value={worker.uid}
                onChange={(e) => updateWorker(index, e.target.value)}
              >
                <option value="">-- Select worker --</option>
                {workers.map((w) => (
                  <option key={w.uid} value={w.uid}>
                    {w.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="worker-remove-btn"
                onClick={() => removeWorker(index)}
                title="Remove worker"
              >
                ×
              </button>
            </div>
          ))}
          <button type="button" className="worker-add-btn" onClick={addWorker}>
            + Add another worker
          </button>
          {assignedWorkers.length > 0 && (
            <div className="worker-chips">
              {assignedWorkers.filter(w => w.uid).map((w, i) => (
                <span key={i} className="worker-chip">{w.displayName}</span>
              ))}
            </div>
          )}
        </div>

        <div className="form-row">
          <label>Job Photos</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
          />
          <div className="photo-previews">
            {jobPhotos.map((p) => (
              <div key={p.id} className="photo-preview">
                <img src={p.preview} alt="preview" />
                <button type="button" onClick={() => removePhoto(p.id)}>×</button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary">
            Save Job
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/jobs')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewJob;
